import { useEffect, useMemo, useRef, useState } from 'react'
import type {
  ResizeObserverEntry,
  ResizeObserverSize
} from '@juggle/resize-observer'
import { ResizeObserver } from '@juggle/resize-observer'

type ObserverRect = Omit<ResizeObserverEntry['contentRect'], 'toJSON'>
type ObserverOptions = Parameters<ResizeObserver['observe']>[1]
export type ElementSize = {
  width: number
  height: number
}
export type ObserverSize = ObserverRect | ElementSize

const defaultState: ElementSize = {
  width: 0,
  height: 0
}

export function useResizeObserver<T extends HTMLElement = any>(
  onResize?: (size: ObserverSize, entry: ResizeObserverEntry) => void,
  options?: ObserverOptions & { enabled?: boolean }
) {
  const { enabled = true, ...observerOptions } = options || {}
  const frameID = useRef(0)
  const ref = useRef<T | null>(null)

  const [rect, setRect] = useState<ObserverSize>(defaultState)

  const observer = useMemo(() => {
    if (!enabled) return

    return new ResizeObserver(entries => {
      const entry = entries[0]

      if (entry) {
        cancelAnimationFrame(frameID.current)

        frameID.current = requestAnimationFrame(() => {
          if (ref.current) {
            let values: ObserverSize

            if (options?.box === 'border-box') {
              const { blockSize, inlineSize } = entry
                .borderBoxSize[0] as ResizeObserverSize
              values = { width: inlineSize, height: blockSize }
            } else if (options?.box === 'content-box') {
              const { blockSize, inlineSize } = entry
                .contentBoxSize[0] as ResizeObserverSize
              values = { width: inlineSize, height: blockSize }
            } else {
              values = entry.contentRect
            }

            onResize?.(values, entry)
            setRect(values)
          }
        })
      }
    })
  }, [])

  useEffect(() => {
    if (!observer || !enabled) return

    if (ref.current) {
      observer.observe(ref.current, observerOptions)
    }

    return () => {
      observer.disconnect()

      if (frameID.current) {
        cancelAnimationFrame(frameID.current)
      }
    }
  }, [ref.current])

  return [ref, rect] as const
}

export function useElementSize<T extends HTMLElement = any>(
  options?: ObserverOptions & { enabled?: boolean }
) {
  const [ref, { width, height }] = useResizeObserver<T>(undefined, options)
  return { ref, width, height }
}
